import { verifyJwt } from './verifyJwt';
import type { JWTClaims } from '../types/auth.types';

interface TokenRefreshConfig {
    refreshThresholdMinutes?: number; // How many minutes before expiry to refresh
    onRefresh: () => Promise<void>; // Callback to trigger refresh
    onError?: (error: Error) => void; // Error callback
}

class TokenRefreshManager {
    private refreshTimer: NodeJS.Timeout | null = null;
    private config: TokenRefreshConfig | null = null;
    private isRefreshing = false;

    /**
     * Start monitoring a JWT token for automatic refresh
     */
    public startRefreshScheduler(token: string, config: TokenRefreshConfig): void {
        this.config = {
            refreshThresholdMinutes: 5, // Default to 5 minutes before expiry
            ...config,
        };

        this.scheduleRefresh(token);
    }

    /**
     * Stop the refresh scheduler
     */
    public stopRefreshScheduler(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        this.config = null;
        this.isRefreshing = false;
    }

    /**
     * Check if a refresh is currently in progress
     */
    public get isCurrentlyRefreshing(): boolean {
        return this.isRefreshing;
    }

    /**
     * Manually trigger a token refresh
     */
    public async manualRefresh(): Promise<void> {
        if (!this.config) {
            throw new Error('Token refresh manager not initialized');
        }

        await this.executeRefresh();
    }

    /**
     * Schedule the next refresh based on token expiration
     */
    private async scheduleRefresh(token: string): Promise<void> {
        try {
            const jwtPayload = await verifyJwt(token);

            if (!jwtPayload) {
                console.warn('Failed to verify JWT for refresh scheduling');
                return;
            }

            const claims = jwtPayload.payload as JWTClaims;
            const expirationTime = claims.exp * 1000; // Convert to milliseconds
            const currentTime = Date.now();
            const timeUntilExpiry = expirationTime - currentTime;

            if (timeUntilExpiry <= 0) {
                console.warn('Token is already expired');
                await this.executeRefresh();
                return;
            }

            // Calculate when to refresh (threshold minutes before expiry)
            const refreshThreshold = this.config!.refreshThresholdMinutes! * 60 * 1000;
            const timeUntilRefresh = Math.max(0, timeUntilExpiry - refreshThreshold);

            // eslint-disable-next-line no-console -- refresh-timer trace with no PII; stripped from production builds
            console.log(
                `Token expires at ${new Date(expirationTime).toISOString()}; refresh in ${Math.round(timeUntilRefresh / 60000)} minutes`
            );

            // Clear any existing timer
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
            }

            // Schedule the refresh
            this.refreshTimer = setTimeout(() => {
                this.executeRefresh();
            }, timeUntilRefresh);
        } catch (error) {
            console.error('Error scheduling token refresh:', error);
            this.config?.onError?.(error as Error);
        }
    }

    /**
     * Execute the token refresh
     */
    private async executeRefresh(): Promise<void> {
        if (this.isRefreshing || !this.config) {
            return;
        }

        this.isRefreshing = true;

        try {
            await this.config.onRefresh();
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.config.onError?.(error as Error);
        } finally {
            this.isRefreshing = false;
        }
    }

    /**
     * Update the scheduler with a new token (after successful refresh)
     */
    public updateToken(newToken: string): void {
        if (this.config) {
            this.scheduleRefresh(newToken);
        }
    }
}

// Export a singleton instance
export const tokenRefreshManager = new TokenRefreshManager();

// Helper function to check if a token is close to expiry
export const isTokenNearExpiry = async (
    token: string,
    thresholdMinutes: number = 5
): Promise<boolean> => {
    try {
        const jwtPayload = await verifyJwt(token);

        if (!jwtPayload) {
            return true; // Consider invalid tokens as "expired"
        }

        const claims = jwtPayload.payload as JWTClaims;
        const expirationTime = claims.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;
        const threshold = thresholdMinutes * 60 * 1000;

        return timeUntilExpiry <= threshold;
    } catch {
        return true; // Consider invalid tokens as "expired"
    }
};
